import { useState } from 'react'
import type { PersonaStatusLevel } from '../lib/types'
import { usePersonaPopup } from './PersonaPopup'

const PERSONA_COLORS: Record<string, string> = {
  forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', chronicle: '#8C6DBF',
  timbre: '#ED9429', griot: '#4E937A', relay: '#4493D4', sentinel: '#999999',
  cairn: '#7A9E7E', joel: '#59C5FA', glean: '#A89060', wrench: '#8B8B8B',
  ember: '#E06040', lumen: '#F5D623', vector: '#5AA0D0', ledger: '#6B8E70',
  beacon: '#D4A520', gretchen: '#B87DB8', vigil: '#708090', herald: '#C0785A',
  grain: '#A5886F', loom: '#7B6FA0', noma: '#E8A87C', counsel: '#6B7B8D',
  pulse: '#60A0B0', cadence: '#9B7A6C', anvil: '#7D7D7D', rivet: '#8C9440',
  reel: '#C73E3E', prism: '#B060B0', tack: '#5080A0', scribe: '#7A8060',
  system: '#484f58',
}

const HUMAN_COLOR = '#59C5FA'

export function isKnownPersona(name: string): boolean {
  const key = (name || '').toLowerCase().replace(/^pu-/, '')
  return key !== '' && key !== 'system' && key in PERSONA_COLORS && key !== 'joel'
}

const STATUS_COLORS: Record<PersonaStatusLevel, string> = {
  active: '#3fb950',   // bright green — awake + has queue/tasks/convs
  idle: '#d29922',     // amber — awake but nothing queued
  sleeping: '#484f58', // gray — not awake
}

interface Props {
  name: string
  size?: number
  showStatus?: boolean
  awake?: boolean
  /** Richer status level. When provided, overrides `awake` for dot color. */
  status?: PersonaStatusLevel
  /** Queue count for a red pip badge. Omitted when 0 or undefined. */
  queueCount?: number
  avatarImage?: boolean
  /** Force human rendering (rounded square, no avatar image attempt). */
  isHuman?: boolean
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  title?: string
}

export default function PersonaAvatar({ name = '', size = 28, showStatus, awake, status, queueCount, isHuman, onClick, title }: Props) {
  const normalizedName = (name || '').toLowerCase().replace(/^pu-/, '')
  const human = isHuman ?? !isKnownPersona(name)
  const color = human ? HUMAN_COLOR : (PERSONA_COLORS[normalizedName] || '#484f58')
  const letter = (name || '?')[0]?.toUpperCase() || '?'
  const [imgFailed, setImgFailed] = useState(false)
  const { hoverProps } = usePersonaPopup()

  const level: PersonaStatusLevel = status ?? (awake ? 'active' : 'sleeping')
  const dotColor = STATUS_COLORS[level]
  const borderRadius = human ? size * 0.25 : '50%'
  const enableHover = !human && isKnownPersona(name)

  const hover = enableHover ? hoverProps(normalizedName) : null

  return (
    <div
      className="relative inline-flex"
      style={{ width: size, height: size, cursor: enableHover || onClick ? 'pointer' : undefined, ...(hover?.style ?? {}) }}
      onClick={onClick}
      title={title}
      onMouseEnter={hover?.onMouseEnter}
      onMouseLeave={hover?.onMouseLeave}
    >
      {!imgFailed ? (
        <img
          src={`/avatars/${normalizedName}.jpg`}
          alt={name}
          className="object-cover"
          style={{ width: size, height: size, borderRadius }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        <div
          className="flex items-center justify-center text-white font-bold"
          style={{
            width: size,
            height: size,
            background: human ? `linear-gradient(135deg, ${HUMAN_COLOR}, ${HUMAN_COLOR}cc)` : color,
            fontSize: size * 0.45,
            borderRadius,
          }}
        >
          {letter}
        </div>
      )}
      {showStatus && (
        <div
          className="absolute -bottom-0.5 -right-0.5 rounded-full border-2"
          style={{
            width: size * 0.35,
            height: size * 0.35,
            background: dotColor,
            borderColor: 'var(--bg-body)',
          }}
        />
      )}
      {showStatus && queueCount != null && queueCount > 0 && (
        <div
          className="absolute -top-0.5 -right-0.5 rounded-full flex items-center justify-center text-white font-bold"
          style={{
            minWidth: Math.max(size * 0.45, 14),
            height: Math.max(size * 0.45, 14),
            padding: '0 3px',
            fontSize: Math.max(size * 0.3, 9),
            background: '#ef4444',
            border: '2px solid var(--bg-body)',
            lineHeight: 1,
          }}
        >
          {queueCount > 9 ? '9+' : queueCount}
        </div>
      )}
    </div>
  )
}

export function PersonaName({ name, children, style, className }: { name: string; children?: React.ReactNode; style?: React.CSSProperties; className?: string }) {
  const { hoverProps } = usePersonaPopup()
  const normalized = (name || '').toLowerCase().replace(/^pu-/, '')
  const known = isKnownPersona(name)
  return (
    <span {...(known ? hoverProps(normalized) : {})} style={style} className={className}>
      {children || name}
    </span>
  )
}
